function formatMoney(value) {
  if (typeof window !== 'undefined' && typeof window.formattedPrice === 'function') {
    return window.formattedPrice(value);
  }

  return '';
}

function getCustomerDiscountValue(tags) {
  if (typeof window !== 'undefined' && typeof window.customerDiscount === 'function') {
    return Number(window.customerDiscount(tags)) || 0;
  }

  return 0;
}

function getDiscountedPrice(priceValue, compareAtPriceValue, customerDiscountValue) {
  const price = Number(priceValue);
  const compareAtPrice = Number(compareAtPriceValue);

  if (!Number.isFinite(price)) return 0;

  if (Number.isFinite(compareAtPrice) && compareAtPrice > 0) {
    return Math.min(compareAtPrice * (1 - customerDiscountValue), price);
  }

  return price * (1 - customerDiscountValue);
}

function getDisplayComparePrice(priceValue, compareAtPriceValue) {
  const price = Number(priceValue);
  const compareAtPrice = Number(compareAtPriceValue);

  if (Number.isFinite(compareAtPrice) && compareAtPrice > 0) return compareAtPrice;

  return Number.isFinite(price) ? price : 0;
}

function escapeHtml(value) {
  if (value == null) return '';

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getSwatchColorKey(swatch) {
  if (!swatch || typeof swatch.color !== 'string') return '';

  return swatch.color.trim().toLowerCase();
}

function mergeSwatchGroup(primary, candidate) {
  const preferred = primary.available ? primary : candidate.available ? candidate : primary;

  return {
    ...preferred,
    color: preferred.color || primary.color || candidate.color || '',
    available: Boolean(primary.available || candidate.available),
    price: preferred.price || primary.price || candidate.price,
    compare_at_price: preferred.compare_at_price || primary.compare_at_price || candidate.compare_at_price,
    image: preferred.image || primary.image || candidate.image,
    hover_image: preferred.hover_image || primary.hover_image || candidate.hover_image,
    swatch_image: preferred.swatch_image || primary.swatch_image || candidate.swatch_image,
    variant_id: preferred.variant_id || primary.variant_id || candidate.variant_id,
  };
}

function getProductSwatches(hit) {
  if (!Array.isArray(hit.swatches)) return [];

  return hit.swatches.filter(Boolean).reduce((merged, swatch) => {
    const colorKey = getSwatchColorKey(swatch);
    if (!colorKey) {
      merged.push(swatch);
      return merged;
    }

    const existingIndex = merged.findIndex((entry) => getSwatchColorKey(entry) === colorKey);
    if (existingIndex === -1) {
      merged.push(swatch);
      return merged;
    }

    merged[existingIndex] = mergeSwatchGroup(merged[existingIndex], swatch);
    return merged;
  }, []).filter((swatch) => swatch.available);
}

function getDefaultSwatch(hit) {
  return getProductSwatches(hit)[0];
}

function isGiftCard(hit) {
  if (hit.gift_card === true) return true;
  if (typeof hit.product_type === 'string' && hit.product_type.toLowerCase().includes('gift card')) return true;
  if (Array.isArray(hit.tags) && hit.tags.some((t) => typeof t === 'string' && t.toLowerCase().includes('gift card'))) return true;
  return false;
}

function getProductUrl(hit) {
  return hit.url || (hit.handle ? `/products/${hit.handle}` : '#');
}

function getProductTitle(hit) {
  return hit.title || hit.name || '';
}

function getProductImage(hit) {
  const defaultSwatch = getDefaultSwatch(hit);
  const featuredImage = hit.featured_image;

  return (
    (defaultSwatch && defaultSwatch.image) ||
    (defaultSwatch && defaultSwatch.hover_image) ||
    hit.image ||
    hit.hover_image ||
    (featuredImage && featuredImage.url) ||
    featuredImage ||
    hit.thumbnail ||
    ''
  );
}

function getProductHoverImage(hit) {
  const defaultSwatch = getDefaultSwatch(hit);

  return (
    (defaultSwatch && defaultSwatch.hover_image) ||
    (defaultSwatch && defaultSwatch.image) ||
    hit.hover_image ||
    ''
  );
}

function getSwatchPrimaryImage(swatch, fallback) {
  return swatch.image || swatch.hover_image || fallback || '';
}

function getSwatchHoverImage(swatch, fallbackHover, fallback) {
  return swatch.hover_image || swatch.image || fallbackHover || fallback || '';
}

function resizeShopifyImage(url, width) {
  if (!url || typeof url !== 'string') return url || '';

  try {
    const u = new URL(url.startsWith('//') ? `https:${url}` : url);
    if (!u.hostname.includes('cdn.shopify.com')) return url;
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch {
    return url;
  }
}

function buildSrcset(url, widths) {
  if (!url) return '';

  return widths
    .map((w) => `${escapeHtml(resizeShopifyImage(url, w))} ${w}w`)
    .join(', ');
}

const IMAGE_WIDTHS = [300, 450, 600, 800];
const IMAGE_SIZES = '(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw';

export function renderProductCard(hit, { queryID = null, position = null, indexName = null } = {}) {
  const url = getProductUrl(hit);
  const title = getProductTitle(hit);
  const swatches = getProductSwatches(hit);
  const activeSwatch = getDefaultSwatch(hit);
  const image = getProductImage(hit);
  const hoverImage = getProductHoverImage(hit);

  const customerDiscountValue = getCustomerDiscountValue(hit.tags);
  const rawPrice = (activeSwatch && activeSwatch.price) || hit.price;
  const rawComparePrice = (activeSwatch && activeSwatch.compare_at_price) || hit.compare_at_price;
  const displayPrice = getDiscountedPrice(rawPrice, rawComparePrice, customerDiscountValue);
  const displayComparePrice = getDisplayComparePrice(rawPrice, rawComparePrice);
  const hasComparePrice = Number(displayComparePrice) > Number(displayPrice);

  const primarySrcset = buildSrcset(image, IMAGE_WIDTHS);
  const primarySrc = resizeShopifyImage(image, 600);

  return `
    <article class="rec-card"
      data-insights-object-id="${escapeHtml(hit.objectID || '')}"
      data-insights-position="${position || ''}"
      data-insights-query-id="${escapeHtml(queryID || '')}"
      data-insights-index="${escapeHtml(indexName || '')}">
      <a class="rec-card__media" href="${escapeHtml(url)}">
        ${image
    ? `<span class="rec-card__image-stack">
            <img class="rec-card__image rec-card__image--primary"
              src="${escapeHtml(primarySrc)}"
              ${primarySrcset ? `srcset="${primarySrcset}" sizes="${IMAGE_SIZES}"` : ''}
              alt="${escapeHtml(title)}"
              width="600" height="769"
              loading="lazy" decoding="async">
            ${hoverImage && hoverImage !== image
    ? `<img class="rec-card__image rec-card__image--hover"
                src="${escapeHtml(resizeShopifyImage(hoverImage, 600))}"
                alt="" loading="lazy" aria-hidden="true" decoding="async">`
    : '<img class="rec-card__image rec-card__image--hover" alt="" aria-hidden="true" hidden>'}
          </span>`
    : '<div class="rec-card__image-placeholder"></div>'}
      </a>
      <div class="rec-card__content">
        ${swatches.length && !isGiftCard(hit)
    ? `<div class="rec-card__swatches" aria-label="Available colors">
            ${swatches.map((swatch) => {
    const isActive = activeSwatch && getSwatchColorKey(activeSwatch) === getSwatchColorKey(swatch);
    const swatchPrice = swatch.price || hit.price;
    const swatchCompare = swatch.compare_at_price || hit.compare_at_price;
    const swatchDisplayPrice = getDiscountedPrice(swatchPrice, swatchCompare, customerDiscountValue);
    const swatchDisplayCompare = getDisplayComparePrice(swatchPrice, swatchCompare);
    const swatchHasSale = Number(swatchDisplayCompare) > Number(swatchDisplayPrice);

    return `
                <button type="button"
                  class="rec-card__swatch${swatch.available ? '' : ' rec-card__swatch--unavailable'}${isActive ? ' is-active' : ''}"
                  title="${escapeHtml(swatch.color || '')}"
                  aria-label="${escapeHtml(`${swatch.color || 'Color'}${swatch.available ? '' : ' unavailable'}`)}"
                  aria-pressed="${isActive ? 'true' : 'false'}"
                  data-swatch-trigger
                  data-color="${escapeHtml(swatch.color || '')}"
                  data-image="${escapeHtml(getSwatchPrimaryImage(swatch, image))}"
                  data-hover-image="${escapeHtml(getSwatchHoverImage(swatch, hoverImage, image))}"
                  data-price="${escapeHtml(formatMoney(swatchDisplayPrice))}"
                  data-compare-price="${swatchHasSale ? escapeHtml(formatMoney(swatchDisplayCompare)) : ''}"
                >
                  ${swatch.swatch_image
    ? `<img class="rec-card__swatch-image"
                      src="${escapeHtml(resizeShopifyImage(swatch.swatch_image, 40))}"
                      alt="${escapeHtml(swatch.color || '')}"
                      width="20" height="20" loading="lazy" decoding="async">`
    : '<span class="rec-card__swatch-fallback" aria-hidden="true"></span>'}
                </button>`;
  }).join('')}
          </div>`
    : ''}
        <h3 class="rec-card__title">
          <a href="${escapeHtml(url)}">${escapeHtml(title)}</a>
        </h3>
        <div class="rec-card__reviews" tabindex="-1">
          <div
            class="yotpo-widget-instance"
            data-yotpo-instance-id="498880"
            data-yotpo-product-id="${escapeHtml(String(hit.id || ''))}"
          ></div>
        </div>
        <div class="rec-card__price">
          <span class="rec-card__price-current${hasComparePrice ? ' rec-card__price-current--sale' : ''}">${escapeHtml(formatMoney(displayPrice))}</span>
          ${hasComparePrice
    ? `<span class="rec-card__price-compare">${escapeHtml(formatMoney(displayComparePrice))}</span>`
    : ''}
        </div>
      </div>
    </article>
  `;
}

function updateCardOnSwatchClick(card, swatchButton) {
  const primaryImage = card.querySelector('.rec-card__image--primary');
  const hoverImage = card.querySelector('.rec-card__image--hover');
  const nextPrimarySrc = swatchButton.dataset.image || '';
  const nextHoverSrc = swatchButton.dataset.hoverImage || '';
  const nextAlt = swatchButton.dataset.color || '';
  const nextPrice = swatchButton.dataset.price || '';
  const nextComparePrice = swatchButton.dataset.comparePrice || '';

  if (primaryImage && nextPrimarySrc) {
    primaryImage.removeAttribute('srcset');
    primaryImage.removeAttribute('sizes');
    primaryImage.src = nextPrimarySrc;
    primaryImage.alt = nextAlt;
  }

  if (hoverImage) {
    if (nextHoverSrc && nextHoverSrc !== nextPrimarySrc) {
      hoverImage.src = nextHoverSrc;
      hoverImage.hidden = false;
    } else {
      hoverImage.hidden = true;
      hoverImage.removeAttribute('src');
    }
  }

  card.querySelectorAll('.rec-card__swatch').forEach((btn) => {
    btn.classList.toggle('is-active', btn === swatchButton);
    btn.setAttribute('aria-pressed', btn === swatchButton ? 'true' : 'false');
  });

  const priceEl = card.querySelector('.rec-card__price-current');
  const comparePriceEl = card.querySelector('.rec-card__price-compare');

  if (priceEl && nextPrice) {
    priceEl.textContent = nextPrice;
    priceEl.classList.toggle('rec-card__price-current--sale', Boolean(nextComparePrice));
  }

  if (comparePriceEl) {
    if (nextComparePrice) {
      comparePriceEl.textContent = nextComparePrice;
      comparePriceEl.hidden = false;
    } else {
      comparePriceEl.hidden = true;
    }
  }
}

export function initSwatchDelegation(container) {
  container.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-swatch-trigger]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const card = trigger.closest('.rec-card');
    if (card) updateCardOnSwatchClick(card, trigger);
  });
}
